/**
 * Canonical module-access authority shared by route, sidebar and action consumers.
 *
 * Authoritative backend profiles always win. Legacy module and pack ACLs are only
 * considered for sessions that have no authoritative profile at all.
 */
const MODULE_ACCESS_LEVELS = Object.freeze(["view", "edit"])

export function hasAuthoritativeAccessProfile(profile) {
  if (!profile) return false

  return Boolean(
    typeof profile.is_superadmin === "boolean" ||
      profile.role_code ||
      profile.source ||
      profile.capabilities ||
      profile.scopes ||
      profile.module_to_pack ||
      profile.module_capabilities ||
      profile.module_capability_overrides ||
      Array.isArray(profile.reports)
  )
}

export function moduleAccessLevelSatisfies(
  grantedLevel,
  requiredLevel = "view"
) {
  if (requiredLevel === "edit") return grantedLevel === "edit"
  return MODULE_ACCESS_LEVELS.includes(grantedLevel)
}

export function resolveBroadModuleAccessLevel(profile, legacyPermissions = {}) {
  if (profile?.is_superadmin) return "edit"
  if (profile?.capabilities?.["modules.edit"] === true) return "edit"
  if (profile?.capabilities?.["modules.view"] === true) return "view"
  if (
    !hasAuthoritativeAccessProfile(profile) &&
    legacyPermissions?.modules === true
  ) {
    return "edit"
  }
  return "none"
}

const isPlainObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

/**
 * Resolve whether the current profile may request the unscoped module inventory.
 *
 * @precondition profile is nullish or a backend-derived access profile; legacyPermissions is the compatibility ACL when no authoritative profile exists.
 * @postcondition Returns true for superadmins, global module access, or any valid module/pack view/edit scope; malformed authoritative scopes fail closed.
 * @mutates none
 */
export function hasAnyModuleAccess(profile, legacyPermissions = {}) {
  if (!hasAuthoritativeAccessProfile(profile)) {
    if (
      legacyPermissions?.modules === true ||
      legacyPermissions?.packs === true
    ) {
      return true
    }
    return [legacyPermissions?.modules, legacyPermissions?.packs].some(
      (value) => Array.isArray(value) && value.length > 0
    )
  }

  if (profile?.is_superadmin) return true
  const scopes = profile?.scopes
  if (!isPlainObject(scopes)) return false
  if (
    Object.keys(scopes).some(
      (scopeType) => scopeType !== "modules" && scopeType !== "packs"
    )
  ) {
    return false
  }

  let hasScopedGrant = false
  for (const scopeType of ["modules", "packs"]) {
    const scopeMap = scopes[scopeType]
    if (!isPlainObject(scopeMap)) return false
    if (
      Object.values(scopeMap).some(
        (level) => level !== "none" && level !== "view" && level !== "edit"
      )
    ) {
      return false
    }
    if (
      Object.values(scopeMap).some(
        (level) => level === "view" || level === "edit"
      )
    ) {
      hasScopedGrant = true
    }
  }

  return (
    hasScopedGrant ||
    profile?.capabilities?.["modules.view"] === true ||
    profile?.capabilities?.["modules.edit"] === true
  )
}

const findModule = (modules, moduleId) =>
  (Array.isArray(modules) ? modules : []).find(
    (item) => Number(item?.module_id ?? item?.id ?? 0) === moduleId
  )

const normalizePackIds = (value) => {
  const values = Array.isArray(value) ? value : [value]
  return [
    ...new Set(
      values
        .map((packId) => Number(packId))
        .filter((packId) => Number.isFinite(packId) && packId > 0)
    ),
  ]
}

/**
 * Return only explicitly permitted pack ids from a canonical scope map.
 * Explicit `none` and unrecognized levels are deny-by-default and must not
 * become navigation grants through the projection layer.
 *
 * @precondition packScopes is a backend-derived `{ packId: level }` map.
 * @postcondition Returns positive numeric ids whose explicit level is `view` or `edit`.
 * @mutates none
 */
export function resolvePackPermissions(packScopes) {
  return [
    ...new Set(
      Object.entries(packScopes || {})
        .filter(([, level]) => MODULE_ACCESS_LEVELS.includes(level))
        .map(([packId]) => Number(packId))
        .filter((packId) => Number.isFinite(packId) && packId > 0)
    ),
  ]
}

const containingPackIdsFor = (profile, matchedModule, moduleId) => {
  const mapped =
    profile?.module_to_pack?.[moduleId] ??
    profile?.module_to_pack?.[String(moduleId)]
  const mappedPackIds = normalizePackIds(mapped)

  const modulePackIds =
    matchedModule?.module_pack_ids ?? matchedModule?.pack_ids
  const inventoryPackIds = Array.isArray(modulePackIds)
    ? normalizePackIds(modulePackIds)
    : normalizePackIds(matchedModule?.module_pack_id ?? matchedModule?.pack_id)

  return [...new Set([...mappedPackIds, ...inventoryPackIds])]
}

const scopeLevelWeight = { none: 0, view: 1, edit: 2 }

const scopeLevelFor = (scopes, scopeId) => {
  if (!scopes || scopeId == null) return null
  const key = String(scopeId)
  if (!Object.prototype.hasOwnProperty.call(scopes, key)) return null
  const level = scopes[key]
  return MODULE_ACCESS_LEVELS.includes(level) ? level : "none"
}

/**
 * Resolve one module's effective level.
 *
 * Precedence for authoritative profiles is superadmin, exact module scope, pack
 * scope, then broad edit/view capability. Legacy grants are intentionally never
 * consulted once an authoritative profile exists.
 */
export function resolveModuleAccessLevel(
  profile,
  moduleId,
  modules = [],
  legacyPermissions = {}
) {
  const normalizedModuleId = Number(moduleId)
  if (!Number.isFinite(normalizedModuleId) || normalizedModuleId <= 0) {
    return "none"
  }

  // Module inventory is an authority boundary. Missing or empty inventory
  // cannot be interpreted as an unrestricted catalogue.
  if (!Array.isArray(modules) || modules.length === 0) {
    return "none"
  }

  const matchedModule = findModule(modules, normalizedModuleId)
  if (!hasAuthoritativeAccessProfile(profile)) {
    if (Array.isArray(modules) && modules.length > 0 && !matchedModule) {
      return "none"
    }
    if (legacyPermissions?.modules === true) return "edit"

    if (
      matchedModule?.module_name &&
      Array.isArray(legacyPermissions?.modules) &&
      legacyPermissions.modules.includes(matchedModule.module_name)
    ) {
      return "edit"
    }

    const legacyPackId = matchedModule?.module_pack_id ?? matchedModule?.pack_id
    if (
      legacyPackId != null &&
      Array.isArray(legacyPermissions?.packs) &&
      legacyPermissions.packs.map(String).includes(String(legacyPackId))
    ) {
      return "edit"
    }

    return "none"
  }

  // A loaded inventory is the backend-projected module boundary. A stale or
  // mistyped direct scope must not create a client-side module authority.
  if (!matchedModule && Array.isArray(modules) && modules.length > 0) {
    return "none"
  }

  if (profile?.is_superadmin) return "edit"

  const moduleScopes = profile?.scopes?.modules
  const directLevel = scopeLevelFor(moduleScopes, normalizedModuleId)
  if (directLevel !== null) return directLevel

  const containingPackIds = containingPackIdsFor(
    profile,
    matchedModule,
    normalizedModuleId
  )

  const hasExplicitPackPolicy =
    Object.keys(profile?.scopes?.packs || {}).length > 0

  if (containingPackIds.length > 0) {
    if (!hasExplicitPackPolicy) return resolveBroadModuleAccessLevel(profile)

    const packLevels = containingPackIds.map(
      (packId) =>
        scopeLevelFor(profile?.scopes?.packs, packId) ??
        resolveBroadModuleAccessLevel(profile)
    )
    return packLevels.reduce(
      (leastPrivileged, level) =>
        scopeLevelWeight[level] < scopeLevelWeight[leastPrivileged]
          ? level
          : leastPrivileged,
      "edit"
    )
  }

  return resolveBroadModuleAccessLevel(profile)
}

const normalizeCapabilityDecision = (decision) => {
  if (decision === true || decision === "allow") return true
  if (decision === false || decision === "deny") return false
  return null
}

const moduleCapabilityDecision = (profile, moduleId, capabilityCode) => {
  const normalizedModuleId = Number(moduleId)
  if (!Number.isFinite(normalizedModuleId) || normalizedModuleId <= 0) {
    return { found: false, decision: null }
  }

  const sources = [
    profile?.module_capability_overrides,
    profile?.module_capabilities,
  ]

  for (const source of sources) {
    if (Array.isArray(source)) {
      const row = source.find(
        (item) =>
          Number(item?.module_id ?? item?.id ?? 0) === normalizedModuleId &&
          (item?.capability_code ?? item?.capability) === capabilityCode
      )
      if (row) {
        return {
          found: true,
          decision: normalizeCapabilityDecision(row.decision) ?? false,
        }
      }
      continue
    }

    const moduleDecisions = source?.[String(normalizedModuleId)]
    if (
      moduleDecisions &&
      Object.prototype.hasOwnProperty.call(moduleDecisions, capabilityCode)
    ) {
      const keyedDecision = moduleDecisions[capabilityCode]
      return {
        found: true,
        decision:
          normalizeCapabilityDecision(
            keyedDecision?.decision ?? keyedDecision
          ) ?? false,
      }
    }
  }

  return { found: false, decision: null }
}

/**
 * Resolve a canonical capability for one module. A module decision identified
 * by module_id and capability_code overrides the global capability; when that
 * row is absent, the global capability is used. The optional fallback capability
 * is used only when the requested global code is not present.
 */
export function resolveModuleCapabilityDecision(
  profile,
  moduleId,
  capabilityCode,
  fallbackCapability = null
) {
  if (!profile || !capabilityCode) return null
  if (profile.is_superadmin) return true

  const moduleDecision = moduleCapabilityDecision(
    profile,
    moduleId,
    capabilityCode
  )
  if (moduleDecision.found) return moduleDecision.decision

  const capabilities = profile.capabilities || {}
  if (Object.prototype.hasOwnProperty.call(capabilities, capabilityCode)) {
    return capabilities[capabilityCode] === true
  }
  if (
    fallbackCapability &&
    Object.prototype.hasOwnProperty.call(capabilities, fallbackCapability)
  ) {
    return capabilities[fallbackCapability] === true
  }

  return null
}
